import { Fragment, useEffect, useId, useRef, useState } from 'react';
import RowActionsMenu from './RowActionsMenu.jsx';

const HIDE = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' };

function cmp(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
}

// A filter's chosen values as a list: a `multiple` filter keeps an array (any of them matches), a plain one a string.
const chosen = (v) => (Array.isArray(v) ? v : (v === '' || v == null ? [] : [v]));

/** Pure pipeline: search (every word), filters (AND across filters, OR within a `multiple` one), then a stable sort. */
export function applyView(rows, { query = '', searchText, filters = [], values = {}, columns = [], sort = null } = {}) {
  const words = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  let out = rows.filter((row) => {
    if (words.length > 0 && searchText) {
      const hay = String(searchText(row) ?? '').toLowerCase();
      if (!words.every((w) => hay.includes(w))) return false;
    }
    return filters.every((f) => {
      const list = chosen(values[f.id]);
      return list.length === 0 || list.some((v) => f.test(row, v));
    });
  });
  if (sort) {
    const col = columns.find((c) => c.id === sort.columnId);
    if (col?.sortValue) {
      const dir = sort.direction === 'descending' ? -1 : 1;
      out = out
        .map((row, i) => ({ row, i }))
        .sort((a, b) => (dir * cmp(col.sortValue(a.row), col.sortValue(b.row))) || (a.i - b.i))
        .map((x) => x.row);
    }
  }
  return out;
}

function SortIcon({ direction }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={`h-3.5 w-3.5 ${direction ? 'text-ipc-primary' : 'text-slate-400'}`} fill="currentColor">
      {direction !== 'descending' ? <path d="M12 4l5 7H7z" /> : null}
      {direction !== 'ascending' ? <path d="M12 20l-5-7h10z" /> : null}
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Paging: by default the table pages with a "Rows per page" select (`pageSizes`). A fixed `pageSize` (opt-in, for
 * example 15 on Security "All checks") instead draws a compact pager: Previous, "1 to 15 of 35", Next, and no select.
 * Either way the page goes back to the first when the search, a filter (also a controlled `filterValues` change from
 * the page) or the sort changes, and a controlled expand of a row on another page (a deep link) turns to that page.
 */
export default function ConsoleTable({
  label, columns, rows, getRowId, rowName = getRowId,
  searchText, searchPlaceholder = 'Search', filters = [], defaultSort = null,
  pageSizes = [25, 50], pageSize: fixedPageSize = null, renderExpanded, expandedIds, onExpandedChange,
  rowActions, actions, caption, loading = false, error = null, onRetry,
  emptyState, hasMore = false, onLoadMore, loadingMore = false,
  filterValues, onFilterChange, framed = true,
  // A real <caption> for the table (screen readers announce it with the table); visually hidden, the card names it.
  tableCaption = null,
}) {
  const baseId = useId();
  const [query, setQuery] = useState('');
  const [innerValues, setInnerValues] = useState({});
  // Filter values are controlled when the caller passes filterValues (a page action such as "Show all failing" sets
  // them); otherwise the table keeps its own.
  const valuesControlled = filterValues !== undefined;
  const values = valuesControlled ? (filterValues ?? {}) : innerValues;
  const setValues = (update) => {
    const next = typeof update === 'function' ? update(values) : update;
    if (valuesControlled) onFilterChange?.(next); else setInnerValues(next);
  };
  const [sort, setSort] = useState(defaultSort);
  const [chosenPageSize, setPageSize] = useState(pageSizes[0]);
  const compactPager = Number.isInteger(fixedPageSize) && fixedPageSize > 0;
  const pageSize = compactPager ? fixedPageSize : chosenPageSize;
  const [page, setPage] = useState(0);
  const [innerExpanded, setInnerExpanded] = useState(() => new Set());
  const detailIds = useRef(new Map());

  const controlled = expandedIds !== undefined;
  const expanded = controlled ? new Set(expandedIds) : innerExpanded;
  const expandable = typeof renderExpanded === 'function';
  const hasActions = typeof rowActions === 'function';
  const detailId = (rowId) => {
    if (!detailIds.current.has(rowId)) detailIds.current.set(rowId, detailIds.current.size + 1);
    return `${baseId}-detail-${detailIds.current.get(rowId)}`;
  };

  const view = applyView(rows, { query, searchText, filters, values, columns, sort });
  const total = view.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const pageRows = view.slice(current * pageSize, current * pageSize + pageSize);
  const showPager = compactPager ? total > pageSize : (total > Math.min(...pageSizes) || hasMore);
  const range = total === 0 ? '0 to 0 of 0' : `${current * pageSize + 1} to ${Math.min(total, (current + 1) * pageSize)} of ${total}`;

  // A filter set from outside the table (controlled filterValues) starts again on the first page, like a chip click.
  const valuesKey = JSON.stringify(values);
  const lastValuesKey = useRef(valuesKey);
  useEffect(() => {
    if (lastValuesKey.current === valuesKey) return;
    lastValuesKey.current = valuesKey;
    setPage(0);
  }, [valuesKey]);

  // A row expanded from outside (a deep link, "Fix first") may sit on another page: turn to it. Declared after the
  // reset above, so when both change together this page wins.
  const expandedKey = controlled ? JSON.stringify(expandedIds ?? []) : '';
  const lastExpanded = useRef([]);
  useEffect(() => {
    const now = controlled && Array.isArray(expandedIds) ? expandedIds : [];
    // Only a newly expanded row turns the page; collapsing one never moves the reader.
    const added = now.filter((id) => !lastExpanded.current.includes(id));
    lastExpanded.current = now;
    if (added.length === 0) return;
    const index = view.findIndex((r) => getRowId(r) === added[added.length - 1]);
    if (index >= 0) setPage(Math.floor(index / pageSize));
    // view and getRowId are this render's values; the effect runs only when the expanded set changes.
  }, [expandedKey]);
  const span = columns.length + (expandable ? 1 : 0) + (hasActions ? 1 : 0);
  const filtered = query.trim() !== '' || Object.values(values).some((v) => chosen(v).length > 0);

  function toggle(id) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    if (controlled) onExpandedChange?.([...next]); else setInnerExpanded(next);
  }

  function onSort(col) {
    setPage(0);
    setSort((s) => {
      if (!s || s.columnId !== col.id) return { columnId: col.id, direction: 'ascending' };
      if (s.direction === 'ascending') return { columnId: col.id, direction: 'descending' };
      return null;
    });
  }

  function clearFilters() {
    setQuery('');
    setValues({});
    setPage(0);
  }

  const chip = (on) => `tap inline-flex items-center justify-center rounded-sm border px-2.5 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light ${on ? 'border-ipc-primary bg-ipc-primary text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`;

  const toolbar = searchText || filters.length > 0 || actions ? (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ipc-border px-3 py-3">
      <div className="flex flex-wrap items-center gap-3">
        {searchText ? (
          <input
            type="search"
            className="input w-full py-1.5 sm:w-64"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); setPage(0); } }}
          />
        ) : null}
        {filters.map((f) => (
          <div key={f.id} role="group" aria-label={f.label} className="flex flex-wrap items-center gap-1">
            <span className="mr-1 text-xs text-slate-600">{f.label}</span>
            {[{ value: '', label: 'All' }, ...f.options].map((o) => {
              const list = chosen(values[f.id]);
              const on = o.value === '' ? list.length === 0 : list.includes(o.value);
              // A `multiple` filter toggles each option on its own (All clears them); a plain one picks one value.
              const next = (v) => {
                if (!f.multiple) return { ...v, [f.id]: o.value };
                const cur = chosen(v[f.id]);
                return { ...v, [f.id]: o.value === '' ? [] : (cur.includes(o.value) ? cur.filter((x) => x !== o.value) : [...cur, o.value]) };
              };
              return (
                <button key={o.value || 'all'} type="button" aria-pressed={on} className={chip(on)} onClick={() => { setValues(next); setPage(0); }}>
                  {o.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  ) : null;

  let body;
  if (error) {
    body = (
      <div role="alert" className="flex flex-wrap items-center gap-3 px-4 py-6 text-sm text-red-700">
        <span>{error}</span>
        {onRetry ? <button type="button" className="btn-secondary px-3 py-1 text-sm" onClick={onRetry}>Try again</button> : null}
      </div>
    );
  } else if (loading && rows.length === 0) {
    body = <p role="status" className="px-4 py-8 text-center text-sm text-slate-600">Loading</p>;
  } else if (rows.length === 0) {
    body = (
      <div className="px-4 py-10 text-center">
        <p className="text-base font-semibold text-slate-800">{emptyState?.title ?? 'Nothing to show'}</p>
        {emptyState?.body ? <p className="mt-1 text-sm text-slate-600">{emptyState.body}</p> : null}
        {emptyState?.action ? <div className="mt-4">{emptyState.action}</div> : null}
      </div>
    );
  } else {
    body = (
      <>
        {caption ? <div className="px-3 pt-3 text-xs text-slate-600">{caption}</div> : null}
        <div role="region" tabIndex={0} aria-label={`${label} table`} className="relative overflow-x-auto">
          <table className="min-w-full text-sm" aria-label={label}>
            {tableCaption ? <caption className="sr-only">{tableCaption}</caption> : null}
            <thead className="bg-ipc-surface text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                {expandable ? <th scope="col" className="w-10 px-2 py-2"><span className="sr-only">Expand</span></th> : null}
                {columns.map((c) => {
                  const sorted = sort?.columnId === c.id ? sort.direction : null;
                  return (
                    <th key={c.id} scope="col" aria-sort={sorted ?? undefined} className={`px-3 py-2 font-medium ${c.hideBelow ? HIDE[c.hideBelow] : ''} ${c.align === 'right' ? 'text-right' : ''}`}>
                      {c.sortValue ? (
                        <button type="button" className="tap inline-flex items-center gap-1 uppercase focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light" onClick={() => onSort(c)}>
                          {c.header}
                          <SortIcon direction={sorted} />
                        </button>
                      ) : c.header}
                    </th>
                  );
                })}
                {hasActions ? <th scope="col" className="w-12 px-2 py-2"><span className="sr-only">Actions</span></th> : null}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const id = getRowId(row);
                const name = rowName(row);
                const open = expandable && expanded.has(id);
                return (
                  <Fragment key={id}>
                    <tr className="border-t border-ipc-border align-top hover:bg-slate-50">
                      {expandable ? (
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-controls={open ? detailId(id) : undefined}
                            aria-label={`${open ? 'Collapse' : 'Expand'} ${name}`}
                            className="tap inline-flex items-center justify-center rounded p-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light"
                            onClick={() => toggle(id)}
                          >
                            <Chevron open={open} />
                          </button>
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td key={c.id} className={`px-3 py-2 ${c.hideBelow ? HIDE[c.hideBelow] : ''} ${c.align === 'right' ? 'text-right' : ''} ${c.className ?? ''}`}>{c.cell(row)}</td>
                      ))}
                      {hasActions ? <td className="px-2 py-2 text-right"><RowActionsMenu name={name} actions={rowActions(row)} /></td> : null}
                    </tr>
                    {open ? (
                      <tr id={detailId(id)}>
                        <td colSpan={span} className="border-t border-ipc-border bg-ipc-surface px-4 py-3">
                          <div role="region" aria-label={`Details for ${name}`}>{renderExpanded(row)}</div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {total === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-700">
            <p>No rows match your search or filters.</p>
            {filtered ? <button type="button" className="btn-secondary mt-3 px-3 py-1 text-sm" onClick={clearFilters}>Clear filters</button> : null}
          </div>
        ) : null}
        {showPager && compactPager ? (
          <nav aria-label={`${label} pages`} className="flex flex-wrap items-center justify-between gap-3 border-t border-ipc-border px-3 py-2 text-[13px] text-ipc-muted" data-testid="table-pager">
            <span className="tabular-nums" aria-live="polite" data-testid="pager-range">{range}</span>
            <span className="flex gap-2">
              <button type="button" className="btn-secondary px-3 py-1 text-sm" aria-label="Previous page" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
              <button type="button" className="btn-secondary px-3 py-1 text-sm" aria-label="Next page" disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>Next</button>
            </span>
          </nav>
        ) : null}
        {showPager && !compactPager ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ipc-border px-3 py-2 text-sm text-slate-700">
            <label className="flex items-center gap-2">
              Rows per page
              <select className="input w-20 py-1" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
                {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span>{range}</span>
            <span className="flex gap-2">
              <button type="button" className="btn-secondary px-3 py-1 text-sm" aria-label="Previous page" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
              <button type="button" className="btn-secondary px-3 py-1 text-sm" aria-label="Next page" disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>Next</button>
            </span>
          </div>
        ) : null}
        {hasMore ? (
          <div className="border-t border-ipc-border px-3 py-2">
            <button type="button" className="btn-secondary px-3 py-1 text-sm" disabled={loadingMore} onClick={onLoadMore}>{loadingMore ? 'Loading...' : 'Load more'}</button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section aria-label={label} className={framed ? 'overflow-hidden rounded-2xl border border-ipc-border bg-white' : 'min-w-0 bg-white'} data-table-framed={framed ? 'true' : 'false'}>
      {toolbar}
      <p data-testid="result-count" className="sr-only" aria-live="polite">{total} {total === 1 ? 'result' : 'results'}</p>
      {body}
    </section>
  );
}
